// The Library's list vocabulary (#1080): columns, filters, sorts and the
// lifecycle and delete wording a pack list and its drawer share. Filtering,
// sorting and paging run on the server; these keys are its `odds`, `status`
// and `sort` values.
import { packRecipeId, scheduledPublishLabel } from './prepackCardModel'

export const ODDS_FILTERS = [
  { key: 'all', label: 'All odds' },
  { key: 'lt300', label: 'Under +300' },
  { key: 'mid', label: '+300 to +600' },
  { key: 'gt600', label: '+600 and up' },
]

export const ALL_COLUMNS = [
  { key: 'sport', label: 'Sport', width: '100px', sortable: 'sport' },
  { key: 'league', label: 'League', width: '85px', sortable: 'league' },
  { key: 'event', label: 'Event', width: 'minmax(220px, 1.4fr)', sortable: 'event' },
  { key: 'type', label: 'Type', width: '140px', sortable: 'type' },
  { key: 'legs', label: 'Legs', width: 'minmax(200px, 1.8fr)', sortable: 'legs' },
  { key: 'odds', label: 'Odds', width: '85px', sortable: 'odds' },
  { key: 'ai', label: 'AI score', width: '90px', sortable: 'ai' },
  { key: 'copies', label: 'Copies', width: '80px', sortable: 'copies' },
  { key: 'expires', label: 'Expires', width: '80px', sortable: 'expires' },
  // The last change, newest first by default (the server's `updated` sort, #1080).
  { key: 'updated', label: 'Updated', width: '80px', sortable: 'updated' },
]

export const DEFAULT_COLUMNS = ['league', 'event', 'type', 'legs', 'odds', 'ai', 'expires']

// The status filter's values, in lifecycle order. A scheduled pack was
// generated early by a before-kickoff schedule, which publishes it at its
// scheduled_at. title_review lists the packs a run saved for title review
// (#984), whatever their status.
export const STATUS_FILTERS = ['all', 'draft', 'scheduled', 'published', 'archived', 'title_review']
export const STATUS_FILTER_LABELS = { all: 'All statuses', title_review: 'Title needs review' }

/** The status filter option's label: "All statuses", "Draft", "Title needs review". */
export const statusFilterLabel = (status) => STATUS_FILTER_LABELS[status] || status.charAt(0).toUpperCase() + status.slice(1)

/** The list params one status choice sends: title_review is a flag over every status (#984). */
export const statusParams = (statusFilter) => (
  statusFilter === 'title_review' ? { status: 'all', title_review: '1' } : { status: statusFilter }
)

export const TEXT_SORT_KEYS = new Set(['sport', 'league', 'event', 'type'])

/** The sort a header click turns `current` into: the same column flips, a new one starts A to Z for text, highest first otherwise. */
export const nextSort = (current, key) => (current.key === key
  ? { key, direction: current.direction === 'asc' ? 'desc' : 'asc' }
  : { key, direction: TEXT_SORT_KEYS.has(key) ? 'asc' : 'desc' })

const plural = (count, noun) => `${count} ${noun}${count === 1 ? '' : 's'}`

// Public delivery serves only published packs on the requested surface, so a
// draft or archived pack with surfaces assigned is not live.
export const isLive = (pack) => pack.status === 'published' && (pack.surfaces || []).length > 0

/** Mirrors the API: deleting needs configure, and a published pack also needs publish. */
export const canDeletePack = (pack, { canConfigure, canPublish }) => (
  canConfigure && (pack.status !== 'published' || canPublish)
)

// A scheduled pack's row status, "Scheduled · publishes Oct 14, 2:00 PM"; null for any other status.
export function scheduledRowStatus(pack) {
  if (pack.status !== 'scheduled') return null
  const when = scheduledPublishLabel(pack)
  return when ? `Scheduled · publishes ${when}` : 'Scheduled'
}

// The drawer's lifecycle button. A scheduled pack can go out now, ahead of
// its time; only a published one can be taken back to a draft.
export const lifecycleLabel = (pack) => (pack.status === 'published' ? 'Unpublish' : pack.status === 'scheduled' ? 'Publish now' : 'Publish')

// Builds the delete confirmation: what goes, what is live, what a schedule
// will refill, and what the operator may not delete. Operator copy says
// "schedule", never "recipe" or "automation" (AGENTS.md).
export function deleteConfirmMessage({ packs, skipped }) {
  const lines = []
  if (packs.length === 1) {
    const [pack] = packs
    lines.push(`Delete “${pack.title}”?`)
    if (isLive(pack)) lines.push(`It is published on ${plural(pack.surfaces.length, 'surface')} and will be removed from the feed immediately.`)
    if (packRecipeId(pack)) lines.push('It was generated by a schedule, which may create a replacement on its next run.')
  } else {
    const live = packs.filter(isLive).length
    const scheduled = packs.filter((pack) => packRecipeId(pack)).length
    if (live > 0) lines.push(`${live} of these ${live === 1 ? 'is' : 'are'} published on feed surfaces and will be removed from the feed immediately.`)
    if (scheduled === 1) lines.push('1 was generated by a schedule, which may create a replacement on its next run.')
    if (scheduled > 1) lines.push(`${scheduled} were generated by schedules, which may create replacements on their next run.`)
  }
  if (skipped > 0) lines.push(`${plural(skipped, 'selected published prepack')} will be skipped — deleting ${skipped === 1 ? 'it' : 'them'} requires publish permission.`)
  lines.push('This cannot be undone.')
  return lines.join(' ')
}
